/**
 * compareMetrics.js — Liste unique des mesures de la comparaison de sessions.
 *
 * Lue par les onglets du graphique, les lignes de seuil et le tableau
 * « Statistiques comparatives » : une mesure ajoutée ici apparaît partout.
 * Seuils PM lus dans pmThresholds.json (PM_THRESHOLDS) : la comparaison
 * classe une valeur comme la page de session et l'alerte e-mail (bornes
 * strictes >, une valeur pile sur un seuil reste au palier inférieur, D169).
 * CO2 (D253, lot 3) : mesure `optional`, présente dans le sélecteur, le
 * tableau et le PDF seulement si une session comparée en a (visibleMetrics) ;
 * une seule référence non alertante (referenceMax), jamais un palier.
 * Module pur : aucun libellé traduit, la page résout les clés i18n.
 */
import { PM_THRESHOLDS, CO2_THRESHOLDS } from '../constants/pmThresholds';
import { semanticColors } from '../constants/semanticColors';
import { isFiniteNumber, percentile } from '../utils/statsUtils';

export const LEVELS = Object.freeze({ NORMAL: 'normal', WARNING: 'warning', ALERT: 'alert' });

const PM_UNIT = 'µg/m³';

// Indicateurs du tableau d'une mesure PM : moyenne, max, P95.
const pmRows = (rowPrefix) => [
  { stat: 'avg', labelKey: `sessions.compare.row.${rowPrefix}avg` },
  { stat: 'max', labelKey: `sessions.compare.row.${rowPrefix}max` },
  { stat: 'p95', labelKey: `sessions.compare.row.${rowPrefix}p95` },
];

const pmMetric = (field, thresholdKey, labelKey, rowPrefix) => ({
  field,
  labelKey,
  notMeasuredKey: `sessions.compare.notMeasured.${thresholdKey}`,
  unit: PM_UNIT,
  tableUnit: PM_UNIT,
  thresholds: {
    warningMax: PM_THRESHOLDS[thresholdKey].warningMax,
    alertMax: PM_THRESHOLDS[thresholdKey].alertMax,
  },
  rows: pmRows(rowPrefix),
  // PM : la valeur la plus basse est la meilleure (coche du tableau).
  lowerIsBetter: true,
});

// field = nom réel du champ des relevés (PMReading : pm1, pm2_5, pm10…).
// notMeasuredKey : mention par mesure (accord du participe en français).
export const COMPARE_METRICS = Object.freeze([
  pmMetric('pm1', 'pm1', 'sessions.compare.metric.pm1', 'pm1'),
  pmMetric('pm2_5', 'pm25', 'sessions.compare.metric.pm25', 'pm25'),
  pmMetric('pm10', 'pm10', 'sessions.compare.metric.pm10', 'pm10'),
  { field: 'temperature', labelKey: 'sessions.compare.metric.temperature',
    notMeasuredKey: 'sessions.compare.notMeasured.temperature', unit: '°C', tableUnit: '°C',
    thresholds: null, rows: [{ stat: 'avg', labelKey: 'sessions.compare.row.tempAvg' }],
    lowerIsBetter: false },
  // Unité traduite : « %HR » en français, « %RH » en anglais (unitKey).
  { field: 'humidity', labelKey: 'sessions.compare.metric.humidity',
    notMeasuredKey: 'sessions.compare.notMeasured.humidity', unit: '%HR',
    unitKey: 'sessions.compare.unit.humidity', tableUnit: '%',
    thresholds: null, rows: [{ stat: 'avg', labelKey: 'sessions.compare.row.humAvg' }],
    lowerIsBetter: false },
  // CO2 en ppm entiers (decimals 0) ; la valeur la plus basse est la meilleure.
  { field: 'co2', labelKey: 'sessions.compare.metric.co2',
    notMeasuredKey: 'sessions.compare.notMeasured.co2', unit: 'ppm', tableUnit: 'ppm', decimals: 0,
    thresholds: null, referenceMax: CO2_THRESHOLDS.referenceMax, rows: pmRows('co2'),
    lowerIsBetter: true, optional: true },
]);

export const DEFAULT_COMPARE_FIELD = 'pm2_5';

// Onglet « Tous les paramètres » (D257) : un graphique par mesure visible.
export const ALL_FIELD = 'all';

export const findMetric = (field) => COMPARE_METRICS.find(m => m.field === field);

/** Unité affichée d'une mesure : clé i18n si elle en a une, sinon symbole invariant. */
export const metricUnit = (metric, t) => (metric.unitKey ? t(metric.unitKey) : metric.unit);

/** Décimales affichées d'une mesure (1 par défaut, 0 pour le CO2). */
export const metricDecimals = (metric) => metric?.decimals ?? 1;

/** Palier d'une valeur : null si non mesurée ou mesure sans seuil. */
export function classifyValue(metric, value) {
  if (!metric?.thresholds || !isFiniteNumber(value)) return null;
  if (value > metric.thresholds.alertMax) return LEVELS.ALERT;
  if (value > metric.thresholds.warningMax) return LEVELS.WARNING;
  return LEVELS.NORMAL;
}

// Texte du tableau (tokens AAA sur fond clair) ; palier normal = couleur par défaut.
const LEVEL_TEXT_COLORS = Object.freeze({
  [LEVELS.WARNING]: semanticColors.amberText,
  [LEVELS.ALERT]: semanticColors.redText,
});

export const levelTextColor = (level) => LEVEL_TEXT_COLORS[level] || null;

/**
 * Lignes de seuil du graphique : [{ kind: 'caution'|'alert'|'reference', value, color }].
 * CO2 : une seule ligne « reference » (valeur max recommandée), ambre comme la
 * courbe CO₂ dédiée, non alertante.
 */
export function thresholdLines(metric) {
  if (isFiniteNumber(metric?.referenceMax)) {
    return [{ kind: 'reference', value: metric.referenceMax, color: semanticColors.amberSolid }];
  }
  if (!metric?.thresholds) return [];
  return [
    { kind: 'caution', value: metric.thresholds.warningMax, color: semanticColors.amberSolid },
    { kind: 'alert', value: metric.thresholds.alertMax, color: semanticColors.redSolid },
  ];
}

/** Moyenne, max, P95 d'un champ ; null partout sans mesure (null ignorés). */
export function metricStats(readings, field) {
  const vals = (readings || []).map(r => r[field]).filter(isFiniteNumber);
  if (!vals.length) return { avg: null, max: null, p95: null };
  return {
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    max: Math.max(...vals),
    p95: percentile(vals, 95),
  };
}

/** Vrai si au moins une session comparée porte une mesure de ce champ. */
export const anySessionMeasures = (sessions, field) =>
  sessions.some(s => (s.readings || []).some(r => isFiniteNumber(r[field])));

/**
 * Mesures à afficher pour ces sessions : toutes, sauf les mesures `optional`
 * (CO2) qu'aucune session ne porte. Sans CO2, la liste est celle d'avant.
 */
export const visibleMetrics = (sessions) =>
  COMPARE_METRICS.filter(m => !m.optional || anySessionMeasures(sessions, m.field));

/**
 * Index des sessions cochées « meilleure valeur » pour une ligne du tableau.
 * Seulement pour une mesure où le plus bas est le meilleur (PM), avec au moins
 * deux valeurs présentes : la ou les sessions au minimum ; aucune si toutes
 * les valeurs présentes sont égales (rien ne se distingue).
 * @param {Array<number|null>} values valeur par session (null = non mesurée)
 * @returns {number[]}
 */
export function bestValueIndexes(values, metric) {
  if (!metric?.lowerIsBetter) return [];
  const present = values.filter(isFiniteNumber);
  if (present.length < 2) return [];
  const min = Math.min(...present);
  if (present.every(v => v === min)) return [];
  return values.reduce((acc, v, i) => (v === min ? [...acc, i] : acc), []);
}

/**
 * Lignes du tableau « Statistiques comparatives » : source unique de l'écran
 * (CompareStatsTable) et du PDF (comparePdfTable), mêmes valeurs des deux côtés.
 * Lignes CO₂ seulement si une session comparée en a (visibleMetrics).
 * @returns {Array<{ metric, stat, labelKey, cells: Array<{ value: number|null,
 *   level: string|null, isBest: boolean }> }>}
 */
export function compareTableRows(loaded) {
  const rows = visibleMetrics(loaded).flatMap(metric => metric.rows.map(row => ({ metric, ...row })));
  return rows.map(row => {
    const values = loaded.map(s => metricStats(s.readings, row.metric.field)[row.stat]);
    const best = bestValueIndexes(values, row.metric);
    return {
      ...row,
      cells: values.map((value, i) => ({
        value, level: classifyValue(row.metric, value), isBest: best.includes(i),
      })),
    };
  });
}
